/**
 * SolidMesh UI State Module
 * Manages UI-specific state (drag modes, selectedShapeType) and viewer wrapper actions.
 * Simple structure creation workflow is in useSimpleStructureCreation composable.
 * Object data is managed by object.ts + SolidMeshObject
 */

import {
	type TSimpleStructureConfirmData,
	useSimpleStructureCreation,
} from "@/components/Project/SolidMesh/composables/useSimpleStructureCreation";
import type { TSimpleStructureType } from "@/models/objects/solidMesh/types";
import type { TSolidMeshLocalAxis } from "earthbrain-viewer";

/** Primitive shape types (3D primitive geometric shapes) */
export type TPrimitiveShapeType = "box" | "sphere" | "cylinder";

/**
 * Unified shape type: covers both primitive shapes, parametric simple structures,
 * and the "custom" fallback (manual/no template selected).
 */
export type TShapeType = TPrimitiveShapeType | TSimpleStructureType | "custom";

export type { TSimpleStructureType };
export type { TSimpleStructureConfirmData };

/** Monotonic per-mesh rotation change signal (replaces rotation polling) */
export type TSolidMeshRotationStamp = Readonly<{
	meshId: string;
	n: number;
}>;

interface ISolidMeshState {
	isDraggingSolidMesh: boolean;
	preDragLocalZ: number | undefined;
	selectedShapeType: TShapeType;
	rotationStamp: TSolidMeshRotationStamp | null;
}

const state: ISolidMeshState = {
	isDraggingSolidMesh: false,
	preDragLocalZ: undefined,
	selectedShapeType: "box",
	rotationStamp: null,
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
	setIsDraggingSolidMesh(state: ISolidMeshState, value: boolean) {
		state.isDraggingSolidMesh = value;
	},
	SetPreDragLocalZ(state: ISolidMeshState, value: number | undefined) {
		state.preDragLocalZ = value;
	},
	setSelectedShapeType(state: ISolidMeshState, value: TShapeType) {
		state.selectedShapeType = value;
	},
	setSolidMeshRotationStamp(state: ISolidMeshState, meshId: string) {
		state.rotationStamp = {
			meshId,
			n: (state.rotationStamp?.n ?? 0) + 1,
		};
	},
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type TVuexCtx = {
	dispatch: (
		type: string,
		payload?: unknown,
		options?: { root?: boolean },
	) => Promise<unknown>;
	commit: (
		type: string,
		payload?: unknown,
		options?: { root?: boolean },
	) => void;
};

/** Lazily resolved composable instance (avoids circular init issues). */
let _creation: ReturnType<typeof useSimpleStructureCreation> | null = null;
/** Mutable ref so closures always use the latest Vuex action context. */
let _latestCtx: TVuexCtx | null = null;

function _resolveCtx(): TVuexCtx {
	if (!_latestCtx)
		throw new Error("_getCreation must be called before closures execute");
	return _latestCtx;
}

function _getCreation(ctx: TVuexCtx) {
	_latestCtx = ctx;
	if (!_creation) {
		// The composable expects a root-level store (commit/dispatch without namespace).
		// Vuex action context has module-scoped commit/dispatch, so we wrap them
		// to always pass { root: true }.  The _latestCtx indirection ensures the
		// closures never hold a stale context reference.
		const rootStore = {
			commit: (type: string, payload?: unknown, options?: { root?: boolean }) =>
				_resolveCtx().commit(type, payload, { ...options, root: true }),
			dispatch: (
				type: string,
				payload?: unknown,
				options?: { root?: boolean },
			) => _resolveCtx().dispatch(type, payload, { ...options, root: true }),
			get state() {
				return (
					_resolveCtx() as unknown as { rootState: Record<string, unknown> }
				).rootState;
			},
		};
		_creation = useSimpleStructureCreation(rootStore);
	}
	return _creation;
}

const actions = {
	setIsDraggingSolidMesh({ commit }, value: boolean) {
		commit("setIsDraggingSolidMesh", value);
	},
	setPreDragLocalZ({ commit }, value: number | undefined) {
		commit("SetPreDragLocalZ", value);
	},

	// -------------------------------------------------------------------------
	// Thin proxy actions — keep Vuex dispatch surface for Options-API callers
	// (ToolPanel.vue, etc.)
	// -------------------------------------------------------------------------

	startSimpleStructureCreation(ctx) {
		_getCreation(ctx).startSimpleStructureCreation();
	},
	cancelSimpleStructureCreation(ctx) {
		return _getCreation(ctx).cancelSimpleStructureCreation();
	},
	clearCreationNotification(ctx) {
		_getCreation(ctx).clearCreationNotification();
	},
	selectSimpleStructureType(ctx, structureType: TSimpleStructureType) {
		return _getCreation(ctx).selectSimpleStructureType(structureType);
	},
	confirmSimpleStructureParams(ctx, data: TSimpleStructureConfirmData) {
		return _getCreation(ctx).confirmSimpleStructureParams(data);
	},
	startManualSolidMeshCreation(ctx) {
		return _getCreation(ctx).startManualSolidMeshCreation();
	},
	cancelManualSolidMeshCreation(ctx) {
		return _getCreation(ctx).cancelManualSolidMeshCreation();
	},

	// Viewer wrapper actions for components to use (prevents direct window.viewer calls from .vue files)
	async applyPreviewParams(
		_,
		{ meshId, params }: { meshId: string; params: Record<string, unknown> },
	) {
		if (!meshId) return;
		return window.viewer.applyPreviewParams(meshId, params);
	},
	updateSolidMeshAttributes(
		_,
		{
			meshId,
			attributes,
		}: { meshId: string; attributes: Record<string, unknown> },
	) {
		if (!meshId) return;
		return window.viewer.updateSolidMeshAttributes(meshId, attributes);
	},
	setSolidMeshColor(
		_,
		{ meshId, colorArr }: { meshId: string; colorArr: number[] },
	) {
		if (!meshId) return;
		return window.viewer.setSolidMeshColor(meshId, colorArr);
	},
	setSolidMeshVisibility(
		_,
		{ meshId, visible }: { meshId: string; visible: boolean },
	) {
		if (!meshId) return;
		return window.viewer.setSolidMeshVisibility(meshId, visible);
	},
	deleteSolidMesh(_, { meshId }: { meshId: string }) {
		if (!meshId) return;
		try {
			return window.viewer.deleteSolidMesh(meshId);
		} catch (e: unknown) {
			console.warn("[SolidMesh] deleteSolidMesh failed:", e);
		}
	},
	getSolidMeshJSON(_, { meshId }: { meshId: string }) {
		if (!meshId) return null;
		return window.viewer.getSolidMeshJSON(meshId);
	},
	getSolidMeshRotationDegrees(
		_,
		{
			meshId,
			reference = null,
			relativeToBaseline = false,
		}: {
			meshId: string;
			reference?: { x: number; y: number; z: number } | null;
			relativeToBaseline?: boolean;
		},
	) {
		if (!meshId) return { x: 0, y: 0, z: 0 };
		return window.viewer.getSolidMeshRotationDegrees(meshId, {
			...(reference ? { reference } : {}),
			relativeToBaseline,
		});
	},
	/**
	 * Two-point structure basis points + baseline-relative bearing, derived in
	 * the viewer from the quaternion (Euler-free — safe for downward pipes).
	 */
	getSolidMeshLocalEndpoints(
		_,
		{ meshId, axis }: Readonly<{ meshId: string; axis: TSolidMeshLocalAxis }>,
	) {
		if (!meshId) return null;
		return window.viewer.getSolidMeshLocalEndpoints(meshId, axis);
	},
	bumpSolidMeshRotationStamp({ commit }, meshId: string) {
		if (!meshId) return;
		commit("setSolidMeshRotationStamp", meshId);
	},
};

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
	isDraggingSolidMesh: (state: ISolidMeshState) => state.isDraggingSolidMesh,
	preDragLocalZ: (state: ISolidMeshState) => state.preDragLocalZ,
	selectedShapeType: (state: ISolidMeshState) => state.selectedShapeType,
	solidMeshRotationStamp: (state: ISolidMeshState) => state.rotationStamp,
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
